import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, CalendarDays, Clock, List, Filter, X, MapPin } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Check } from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOURS = Array.from({ length: 17 }, (_, i) => i + 6); // 6am to 10pm

function pad(n) { return String(n).padStart(2, "0"); }

function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }

function fmtDate(d) { return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`; }

function fmtMonthYear(d) {
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function getWeekStart(d) {
  const r = new Date(d); r.setDate(r.getDate() - r.getDay()); return r;
}

function getDaysInMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

function getMonthStart(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

const statusColors = {
  CONFIRMED: "bg-green-500", COMPLETED: "bg-green-600", PENDING_CASH: "bg-amber-500",
  PENDING_PAYMENT: "bg-amber-400", CANCELLED: "bg-red-400", open: "bg-green-500",
  completed: "bg-slate-400", cancelled: "bg-red-400", ACTIVE: "bg-purple-500",
  REGISTERED: "bg-green-500", PAID: "bg-green-600", pending: "bg-amber-500",
};

// FilterPill - compact filter dropdown
function FilterPill({ label, value, options, onSelect, onClear, ...props }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button {...props}
          className={`flex items-center gap-1.5 h-7 px-2.5 border-2 text-[10px] font-heading uppercase tracking-wide transition-colors
            ${value ? 'border-court-orange bg-court-orange/5 text-court-orange' : 'border-slate-200 text-muted-foreground hover:border-slate-300'}`}>
          <Filter className="w-3 h-3" />
          {value ? (
            <>
              <span className="max-w-[80px] truncate">{options.find(o => o.value === value)?.label || value}</span>
              <span onClick={(e) => { e.stopPropagation(); onClear(); }} className="hover:text-red-500 ml-0.5"><X className="w-3 h-3" /></span>
            </>
          ) : label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-0" align="end">
        <Command>
          <CommandInput placeholder={`Search ${label.toLowerCase()}...`} className="h-9 text-xs" />
          <CommandList className="max-h-[200px]">
            <CommandEmpty className="py-3 text-center text-xs text-muted-foreground">No results</CommandEmpty>
            <CommandGroup>
              {options.map(opt => (
                <CommandItem key={opt.value} value={opt.label} onSelect={() => { onSelect(opt.value); setOpen(false); }}
                  className="flex items-center gap-2 text-xs cursor-pointer px-2 py-1.5">
                  <span className="flex-1 capitalize">{opt.label}</span>
                  {value === opt.value && <Check className="w-3.5 h-3.5 text-court-orange" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function CalendarView({ items = [], onItemClick, title = "Calendar", defaultView = "month" }) {
  const [view, setView] = useState(defaultView); // day, week, month
  const [currentDate, setCurrentDate] = useState(new Date());

  const today = fmtDate(new Date());

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (view === "day") d.setDate(d.getDate() + dir);
    else if (view === "week") d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setCurrentDate(d);
  };

  const goToday = () => setCurrentDate(new Date());

  // Filters
  const [venueFilter, setVenueFilter] = useState("");
  const [sportFilter, setSportFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState(""); // booking, event, session

  // Extract unique venues and sports from items
  const uniqueVenues = useMemo(() => [...new Set(items.map(i => i.venue).filter(Boolean))].sort(), [items]);
  const uniqueSports = useMemo(() => [...new Set(items.map(i => i.sport).filter(Boolean))].sort(), [items]);
  const uniqueTypes = useMemo(() => [...new Set(items.map(i => i.type).filter(Boolean))].sort(), [items]);

  const activeFilterCount = [venueFilter, sportFilter, typeFilter].filter(Boolean).length;

  const clearFilters = () => { setVenueFilter(""); setSportFilter(""); setTypeFilter(""); };

  // Apply filters then group by date
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      if (venueFilter && item.venue !== venueFilter) return false;
      if (sportFilter && item.sport !== sportFilter) return false;
      if (typeFilter && item.type !== typeFilter) return false;
      return true;
    });
  }, [items, venueFilter, sportFilter, typeFilter]);

  // Group items by date
  const itemsByDate = useMemo(() => {
    const map = {};
    filteredItems.forEach(item => {
      const d = item.date;
      if (!map[d]) map[d] = [];
      map[d].push(item);
    });
    return map;
  }, [filteredItems]);

  // Month View
  const MonthView = () => {
    const ms = getMonthStart(currentDate);
    const daysInMonth = getDaysInMonth(currentDate);
    const startDay = ms.getDay();
    const cells = [];
    // Empty cells before month start
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${currentDate.getFullYear()}-${pad(currentDate.getMonth()+1)}-${pad(d)}`;
      cells.push({ day: d, date: dateStr, items: itemsByDate[dateStr] || [] });
    }

    return (
      <div>
        <div className="grid grid-cols-7 border-b border-slate-200">
          {DAYS.map(d => <div key={d} className="py-2 text-center text-[10px] font-heading uppercase tracking-wider text-muted-foreground">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((cell, i) => (
            <div key={i} onClick={() => cell && setCurrentDate(new Date(cell.date)) || undefined}
              className={`min-h-[80px] md:min-h-[100px] border-b border-r border-slate-100 p-1 cursor-pointer hover:bg-slate-50 transition-colors
                ${!cell ? 'bg-slate-50/50' : ''}
                ${cell?.date === today ? 'bg-court-orange/5' : ''}`}>
              {cell && (
                <>
                  <div className={`text-xs font-heading font-bold mb-1 ${cell.date === today ? 'text-court-orange' : 'text-foreground'}`}>
                    {cell.day}
                  </div>
                  <div className="space-y-0.5">
                    {cell.items.slice(0, 3).map((item, j) => (
                      <div key={j} onClick={(e) => { e.stopPropagation(); onItemClick?.(item); }}
                        className={`text-[9px] md:text-[10px] truncate px-1 py-0.5 text-white rounded-sm ${statusColors[item.status] || 'bg-slate-400'}`}
                        style={{ backgroundColor: item.color || undefined }}
                        data-testid={`cal-item-${item.id}`}>
                        {item.start} {item.title}
                      </div>
                    ))}
                    {cell.items.length > 3 && (
                      <div className="text-[9px] text-muted-foreground px-1">+{cell.items.length - 3} more</div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Week View
  const WeekView = () => {
    const ws = getWeekStart(currentDate);
    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const d = addDays(ws, i);
      return { date: fmtDate(d), dayName: DAYS[d.getDay()], dayNum: d.getDate(), isToday: fmtDate(d) === today };
    });

    return (
      <div className="overflow-x-auto">
        <div className="min-w-[700px]">
          {/* Header */}
          <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-slate-200">
            <div className="py-2 text-center text-[10px] font-heading uppercase text-muted-foreground"></div>
            {weekDays.map(d => (
              <div key={d.date} className={`py-2 text-center border-l border-slate-100 ${d.isToday ? 'bg-court-orange/5' : ''}`}>
                <div className="text-[10px] font-heading uppercase tracking-wider text-muted-foreground">{d.dayName}</div>
                <div className={`text-lg font-heading font-bold ${d.isToday ? 'text-court-orange' : ''}`}>{d.dayNum}</div>
              </div>
            ))}
          </div>
          {/* Time grid */}
          {HOURS.map(h => (
            <div key={h} className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-slate-50 min-h-[48px]">
              <div className="py-1 pr-2 text-right text-[10px] text-muted-foreground font-mono">{pad(h)}:00</div>
              {weekDays.map(d => {
                const dayItems = (itemsByDate[d.date] || []).filter(item => {
                  const ih = parseInt(item.start?.slice(0, 2) || "0");
                  return ih === h;
                });
                return (
                  <div key={d.date} className={`border-l border-slate-100 px-0.5 py-0.5 ${d.isToday ? 'bg-court-orange/[0.02]' : ''}`}>
                    {dayItems.map((item, j) => (
                      <div key={j} onClick={() => onItemClick?.(item)}
                        className="text-[10px] truncate px-1 py-0.5 mb-0.5 text-white rounded-sm cursor-pointer hover:opacity-80"
                        style={{ backgroundColor: item.color || "#94A3B8" }}
                        data-testid={`week-item-${item.id}`}>
                        {item.start} {item.title}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Day View
  const DayView = () => {
    const dateStr = fmtDate(currentDate);
    const dayItems = itemsByDate[dateStr] || [];
    const isToday = dateStr === today;

    return (
      <div>
        <div className={`text-center py-3 border-b border-slate-200 ${isToday ? 'bg-court-orange/5' : ''}`}>
          <div className="text-[10px] font-heading uppercase tracking-wider text-muted-foreground">
            {currentDate.toLocaleDateString("en-US", { weekday: "long" })}
          </div>
          <div className={`text-3xl font-heading font-bold ${isToday ? 'text-court-orange' : ''}`}>
            {currentDate.getDate()}
          </div>
          <div className="text-xs text-muted-foreground">{fmtMonthYear(currentDate)}</div>
        </div>
        {dayItems.length === 0 ? (
          <div className="text-center py-16">
            <CalendarDays className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="font-heading text-sm text-muted-foreground uppercase">No items on this day</p>
          </div>
        ) : (
          <div className="space-y-0">
            {HOURS.map(h => {
              const hourItems = dayItems.filter(item => parseInt(item.start?.slice(0, 2) || "0") === h);
              if (hourItems.length === 0) return (
                <div key={h} className="flex border-b border-slate-50 min-h-[40px]">
                  <div className="w-16 py-2 pr-2 text-right text-[10px] text-muted-foreground font-mono shrink-0">{pad(h)}:00</div>
                  <div className="flex-1 border-l border-slate-100"></div>
                </div>
              );
              return (
                <div key={h} className="flex border-b border-slate-100">
                  <div className="w-16 py-2 pr-2 text-right text-[10px] text-muted-foreground font-mono shrink-0">{pad(h)}:00</div>
                  <div className="flex-1 border-l border-slate-100 p-1 space-y-1">
                    {hourItems.map((item, j) => (
                      <div key={j} onClick={() => onItemClick?.(item)}
                        className="p-2 text-white rounded-sm cursor-pointer hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: item.color || "#94A3B8" }}
                        data-testid={`day-item-${item.id}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-heading text-xs font-bold uppercase">{item.title}</span>
                          <Badge className="bg-white/20 text-white text-[9px]">{item.type}</Badge>
                        </div>
                        <div className="flex items-center gap-3 text-[10px] mt-1 opacity-80">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{item.start} - {item.end}</span>
                          {item.venue && <span>{item.venue}</span>}
                          {item.sport && <span className="capitalize">{item.sport}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="calendar-view">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button data-testid="cal-prev" variant="outline" size="sm" onClick={() => navigate(-1)} className="rounded-full border-2 w-8 h-8 p-0">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <h2 className="font-heading text-lg md:text-xl font-bold uppercase tracking-tight min-w-[200px] text-center">
            {view === "day" ? currentDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) :
             view === "week" ? `Week of ${getWeekStart(currentDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` :
             fmtMonthYear(currentDate)}
          </h2>
          <Button data-testid="cal-next" variant="outline" size="sm" onClick={() => navigate(1)} className="rounded-full border-2 w-8 h-8 p-0">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button data-testid="cal-today" variant="outline" size="sm" onClick={goToday} className="rounded-full border-2 text-xs font-heading uppercase px-3 h-8 ml-2">
            Today
          </Button>
        </div>
        <div className="flex border-2 border-slate-200 divide-x-2 divide-slate-200">
          {[
            { value: "day", icon: List, label: "Day" },
            { value: "week", icon: CalendarDays, label: "Week" },
            { value: "month", icon: CalendarDays, label: "Month" },
          ].map(v => (
            <button key={v.value} data-testid={`view-${v.value}`} onClick={() => setView(v.value)}
              className={`px-3 py-1.5 text-xs font-heading uppercase tracking-wide transition-colors flex items-center gap-1
                ${view === v.value ? 'bg-court-orange text-white' : 'text-muted-foreground hover:bg-slate-50'}`}>
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Legend + Filters */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex gap-4 text-[10px]">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#EA580C]" />Bookings</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#8B5CF6]" />Events</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#3B82F6]" />Sessions</span>
          {activeFilterCount > 0 && (
            <span className="text-muted-foreground">Showing {filteredItems.length} of {items.length} items</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Type Filter */}
          {uniqueTypes.length > 1 && (
            <FilterPill data-testid="filter-type" label="Type" value={typeFilter} options={uniqueTypes.map(t => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))} onSelect={setTypeFilter} onClear={() => setTypeFilter("")} />
          )}
          {/* Venue Filter */}
          {uniqueVenues.length > 1 && (
            <FilterPill data-testid="filter-venue" label="Venue" value={venueFilter} options={uniqueVenues.map(v => ({ value: v, label: v }))} onSelect={setVenueFilter} onClear={() => setVenueFilter("")} />
          )}
          {/* Sport Filter */}
          {uniqueSports.length > 1 && (
            <FilterPill data-testid="filter-sport" label="Sport" value={sportFilter} options={uniqueSports.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))} onSelect={setSportFilter} onClear={() => setSportFilter("")} />
          )}
          {activeFilterCount > 0 && (
            <Button data-testid="clear-all-filters" variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-red-500 hover:text-red-600 h-7 px-2">
              <X className="w-3 h-3 mr-1" />Clear
            </Button>
          )}
        </div>
      </div>

      {/* Calendar Content */}
      <div className="bg-white border-2 border-slate-100 overflow-hidden">
        {view === "month" && <MonthView />}
        {view === "week" && <WeekView />}
        {view === "day" && <DayView />}
      </div>
    </div>
  );
}
